// ═══════════════════════════════════════════════════════════════════════════════
// src/hooks/usePracticePreferences.js
//
// Single source of truth for practice-level wizard preferences on the client.
// Powers Tier 2 UI gates: sidebar visibility, route guards, and picker filters
// keyed off the same prefs the wizard writes (and the same ones Tier 1 RPCs
// like practice_ai_feature_enabled read on the server).
//
// Server-state lives in TanStack Query (see main.jsx QueryClientProvider),
// keyed by practice_id. Multiple components calling the hook share one cache
// entry and one in-flight fetch. This replaced a hand-rolled module-level Map
// that had three defects the query cache fixes for free:
//   1. It survived SPA sign-out (only a full reload cleared it), so a shared
//      workstation could hand the next user the previous practice's prefs.
//      Now cleared by AuthProvider on every sign-out path.
//   2. Errors were never cached, so a failing fetch re-fired on every mount.
//   3. Nothing invalidated it after the wizard saved, so a changed preference
//      was invisible until a hard reload. See invalidatePracticePreferences.
// ═══════════════════════════════════════════════════════════════════════════════

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { supabase } from "../lib/supabaseClient";
import { fetchPracticePreferences } from "../lib/practicePreferencesApi";

const EMPTY = Object.freeze({});

export const practicePreferencesKey = (practiceId) => ["practice-preferences", practiceId ?? null];

// Preferences change only when a settings screen or the wizard saves, and
// those paths update or invalidate the cache, so it can be long-lived.
export const PRACTICE_PREFERENCES_STALE_MS = 5 * 60 * 1000;

export function usePracticePreferences() {
  const { practiceId } = useAuth();

  const { data, isFetching, isFetched } = useQuery({
    queryKey: practicePreferencesKey(practiceId),
    queryFn: () => fetchPracticePreferences(practiceId),
    // No practice yet (auth still resolving, or signed out): resolve to empty
    // immediately instead of hitting the DB. Matches the original hook, which
    // set prefs=null / loaded=true for a null practiceId.
    enabled: !!practiceId,
    // Preferences change only when the wizard saves, and that path calls
    // invalidatePracticePreferences(), so the cache can be long-lived.
    staleTime: PRACTICE_PREFERENCES_STALE_MS,
    // Load failure -> {} (a permissive default), exactly as before. Surface it
    // via the same warning the old hook used so behavior stays identical.
    retry: 1,
  });

  // `loaded` semantics preserved exactly:
  //   - null practiceId  -> loaded (nothing to load)
  //   - otherwise        -> loaded once the query has settled (data or error)
  const loaded = !practiceId || isFetched;

  return {
    prefs: data || EMPTY,
    loaded,
    // Exposed for callers that gate on "still loading". The original hook did
    // not return this, so one consumer read `loading` and always got
    // undefined; it now gets a real boolean.
    loading: !!practiceId && isFetching && !isFetched,
  };
}

// Call after the wizard (or any settings page) writes practice_preferences so
// every mounted consumer sees the new value without a hard reload.
export function useInvalidatePracticePreferences() {
  const queryClient = useQueryClient();
  const { practiceId } = useAuth();
  return () => queryClient.invalidateQueries({ queryKey: practicePreferencesKey(practiceId) });
}

// Read the preferences blob for a screen that EDITS it (the onboarding
// wizard, Practice info's staff alert emails), through the same cache entry
// as usePracticePreferences (round 7 #8). Those screens used to run their own
// select on open, next to the cached hook's, so Practice info read
// practice_preferences three times.
//
// The cached read turns a failure, and a practice with no row yet, into {}
// (permissive gates). An editor must tell those apart: it shows "Couldn't
// load" with Retry on a failure, and the wizard inserts the first row. So:
//   - a fresh, non-empty cached blob (or the in-flight cached read, which
//     fetchQuery joins rather than repeats) is used as is: no extra request;
//   - an empty one, or { fresh: true } (Retry), falls through to a strict
//     read that throws on error and reports whether the row exists. That
//     read also refreshes the cache entry, so other consumers do not repeat it.
// Returns { preferences, exists }. Throws on a failed read.
export async function readPracticePreferencesForEdit(queryClient, practiceId, { fresh = false } = {}) {
  const key = practicePreferencesKey(practiceId);
  if (!fresh) {
    try {
      const cached = await queryClient.fetchQuery({
        queryKey: key,
        queryFn: () => fetchPracticePreferences(practiceId),
        staleTime: PRACTICE_PREFERENCES_STALE_MS,
      });
      if (cached && typeof cached === "object" && Object.keys(cached).length > 0) {
        return { preferences: cached, exists: true };
      }
    } catch (e) {
      // Fall through to the strict read, which reports the real error.
    }
  }
  const { data, error } = await supabase
    .from("practice_preferences")
    .select("preferences")
    .eq("practice_id", practiceId)
    .maybeSingle();
  if (error) throw new Error(error.message || "Preferences load failed");
  const preferences = (data && data.preferences) || {};
  queryClient.setQueryData(key, preferences);
  return { preferences, exists: !!data };
}

// Read a dotted path out of the preferences JSONB. Returns the default if
// any segment is missing or the final value is null/undefined.
export function getPref(prefs, path, dflt) {
  if (!prefs || !path) return dflt;
  const parts = String(path).split(".");
  let cur = prefs;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return dflt;
    cur = cur[p];
  }
  return (cur === undefined || cur === null) ? dflt : cur;
}

// Mirror of public.practice_ai_feature_enabled(practice_id, feature_key)
// used by the Tier 1 EF gates. Returns true ONLY when the boolean is
// explicitly true; missing or non-boolean values count as disabled, matching
// server-side behavior so what the user sees in the rail matches what the
// EF will actually run.
export function isAiFeatureEnabled(prefs, featureKey) {
  if (!featureKey) return false;
  const v = getPref(prefs, "ai_features.features." + featureKey + ".enabled", false);
  return v === true;
}

// HRSN master switch. Same path the wizard writes and Tier 1 reads server-side.
export function isHrsnEnabled(prefs) {
  return getPref(prefs, "hrsn.enabled", false) === true;
}

// Care Management delivery model. "None" = practice does not deliver CM,
// hide all CM nav and views. Other values: "Direct", "AMHPlus", "CMA",
// "CIN" depending on wizard flow.
export function getCmDeliveryModel(prefs) {
  return String(getPref(prefs, "care_management.cm_delivery_model", "None"));
}
